import { useTranslation } from 'react-i18next'

import { Heart, Pill, Scissors, Truck, UtensilsCrossed } from 'lucide-react'

import { AdditionalService, ServiceType } from '@monorepo/functions/src/types/services'

const getServiceIcon = (serviceType: ServiceType) => {
    switch (serviceType) {
        case 'driver':
            return <Truck className='h-4 w-4 text-blue-500' />
        case 'special_food':
            return <UtensilsCrossed className='h-4 w-4 text-orange-500' />
        case 'medication':
            return <Pill className='h-4 w-4 text-red-500' />
        case 'special_care':
            return <Heart className='h-4 w-4 text-pink-500' />
        case 'hairdressing':
            return <Scissors className='size-4 text-purple-500' />
        default:
            return null
    }
}

const translateService = (service: AdditionalService, t: (key: string, defaultValue: string) => string) => {
    switch (service.type) {
        case 'driver':
            return t('services.driver', 'Chofer')
        case 'special_food':
            return t(
                'services.specialFood',
                'Comida especial ({{type}})',
                // @ts-expect-error
                {
                    type:
                        service.foodType === 'refrigerated'
                            ? t('services.specialFood.refrigerated', 'Refrigerada')
                            : t('services.specialFood.frozen', 'Congelada'),
                },
            )
        case 'medication':
            return t('services.medication', 'Medicación')
        case 'special_care':
            return t('services.specialCare', 'Cuidados especiales')
        case 'hairdressing':
            return (
                t('services.hairdressing', 'Peluquería') +
                ': ' +
                service.services
                    .map(s => {
                        switch (s) {
                            case 'bath_and_brush':
                                return t('services.hairdressing.bathAndBrush', 'Baño y cepillado')
                            case 'bath_and_trim':
                                return t('services.hairdressing.bathAndTrim', 'Baño y corte')
                            case 'stripping':
                                return t('services.hairdressing.stripping', 'Stripping')
                            case 'deshedding':
                                return t('services.hairdressing.deshedding', 'Deslanado')
                            case 'brushing':
                                return t('services.hairdressing.brushing', 'Cepillado')
                            case 'spa':
                                return t('services.hairdressing.spa', 'Spa')
                            case 'spa_ozone':
                                return t('services.hairdressing.spaOzone', 'Spa con ozono')
                            case 'knots':
                                return t('services.hairdressing.knots', 'Nudos')
                            case 'nail_trimming':
                                return t('services.hairdressing.nailTrimming', 'Nail trimming')
                            default:
                                return s
                        }
                    })
                    .join(', ')
            )
        default:
            return t('services.unknown', 'Servicio desconocido')
    }
}

interface ServiceItemProps {
    service: AdditionalService
    className?: string
}

export function ServiceItem({ service, className = '' }: ServiceItemProps) {
    const { t } = useTranslation()

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            {getServiceIcon(service.type)}
            <span className='flex-1 text-sm text-muted-foreground'>{translateService(service, t)}</span>
        </div>
    )
}
